/**
 * Agent stream client.
 *
 * Ports the transport SPMB uses (backend/api/v1/endpoints/chat.py +
 * frontend/src/app/services/stream.service.ts) from Angular/RxJS to a plain
 * async generator.
 *
 * The important property carried over is that the stream is *resumable*: the
 * backend buffers every event under a monotonic `seq`, and a reader reattaches
 * with `?after_seq=N` to replay what it missed before going live. A dropped
 * connection or a page refresh mid-answer therefore resumes instead of losing
 * the run. `fetch` + ReadableStream is used rather than `EventSource` because
 * EventSource cannot send an Authorization header.
 */

export type StreamEvent =
  | { type: "token"; seq: number; text: string }
  | { type: "tool_start"; seq: number; tool: string; label: string }
  | { type: "tool_end"; seq: number; tool: string; ok: boolean }
  | { type: "message"; seq: number; role: "assistant"; text: string }
  // The server discarded what it had streamed and is substituting this.
  // Emitted when an outgoing response failed a safety check server-side.
  | { type: "replace"; seq: number; text: string }
  // A file the agent produced. Delivered as an event rather than a URL in the
  // reply, because a model cannot be relied on to echo a link verbatim.
  // A note to the team, drafted for the visitor to check and send.
  | { type: "call_request"; seq: number; draft: string; email: string }
  | {
      type: "artifact";
      seq: number;
      url: string;
      filename: string;
      rows: number;
    }
  | { type: "error"; seq: number; message: string }
  | { type: "done"; seq: number };

// 8080 is the backend. The frontend serves on 8000, and pointing the fallback
// there sends every call to the dev server, which has no /api route.
export const API_ORIGIN =
  process.env.GATSBY_AGENT_API_ORIGIN || "http://localhost:8080";

const API_BASE = `${API_ORIGIN}/api/v1/chat`;

export type StartRunResult = {
  run_id: string;
  session_id: string;
  /** Returned only when this call created the session. Keep it; it is the
   *  proof of ownership for every later request, and is never sent again. */
  session_token?: string;
};

/** Opens a run for `message` and returns its id; events arrive over `attachRun`. */
export const startRun = async (
  message: string,
  sessionId: string | null,
  uploadIds: string[] = [],
  signal?: AbortSignal,
  sessionToken?: string | null,
  recaptchaToken?: string | null
): Promise<StartRunResult> => {
  const response = await fetch(`${API_BASE}/runs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(sessionToken ? { "X-Session-Token": sessionToken } : {}),
    },
    body: JSON.stringify({
      message,
      session_id: sessionId,
      // Named explicitly rather than left to "all pending", so a file the
      // visitor removed while another was still uploading cannot ride along.
      upload_ids: uploadIds,
      // Only read by the backend when this starts a new session.
      recaptcha_token: recaptchaToken ?? null,
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Could not start run (${response.status})`);
  }
  return response.json();
};

/**
 * Attaches to a run's event stream, yielding events as they arrive.
 *
 * Replays buffered events after `afterSeq` first, then streams live. Returns
 * immediately on 204, which the backend uses to say "this run has no active
 * stream" - a completed run rather than an error.
 */
export async function* attachRun(
  runId: string,
  afterSeq = 0,
  signal?: AbortSignal
): AsyncGenerator<StreamEvent> {
  const response = await fetch(
    `${API_BASE}/runs/${runId}/events?after_seq=${afterSeq}`,
    { headers: { Accept: "text/event-stream" }, signal }
  );

  if (response.status === 204) return;
  if (!response.ok || !response.body) {
    throw new Error(`Stream failed (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line. Anything after the last
      // separator is a partial frame and stays in the buffer.
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";

      for (const frame of frames) {
        const dataLines = frame
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim());

        if (dataLines.length === 0) continue;

        const payload = dataLines.join("\n");
        if (payload === "[DONE]") return;

        try {
          yield JSON.parse(payload) as StreamEvent;
        } catch {
          // A malformed frame must not kill an otherwise healthy stream.
          // eslint-disable-next-line no-console
          console.warn("Discarding unparseable stream frame", payload);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}


// --- Uploads ----------------------------------------------------------------

export type Upload = {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  session_id: string;
  session_token?: string;
};

/**
 * Types the backend accepts, mirroring its allowlist. Used for the file
 * input's `accept` attribute, which is a convenience for the picker only - the
 * server re-validates, because `accept` is trivially bypassed by drag-and-drop.
 */
export const ACCEPTED_FILE_TYPES = [
  ".pdf",
  ".doc",
  ".docx",
  ".ppt",
  ".pptx",
  ".xls",
  ".xlsx",
  ".rtf",
  ".txt",
  ".csv",
  ".tsv",
  ".md",
  ".html",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".heic",
  ".heif",
].join(",");

export const MAX_FILE_MB = 50;

/** Reads the server's error text so the visitor sees why a file was refused. */
const errorFrom = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.json();
    return typeof body?.detail === "string" ? body.detail : fallback;
  } catch {
    return fallback;
  }
};

export const uploadFile = async (
  file: File,
  sessionId: string | null,
  signal?: AbortSignal,
  sessionToken?: string | null
): Promise<Upload> => {
  const form = new FormData();
  form.append("file", file);
  if (sessionId) form.append("session_id", sessionId);

  const response = await fetch(`${API_BASE}/uploads`, {
    method: "POST",
    body: form,
    signal,
    headers: sessionToken ? { "X-Session-Token": sessionToken } : undefined,
  });

  if (!response.ok) {
    throw new Error(await errorFrom(response, `Could not upload ${file.name}.`));
  }
  return response.json();
};

export const deleteUpload = async (
  uploadId: string,
  sessionId: string,
  sessionToken?: string | null
): Promise<void> => {
  const response = await fetch(
    `${API_BASE}/uploads/${uploadId}?session_id=${encodeURIComponent(sessionId)}`,
    {
      method: "DELETE",
      headers: sessionToken ? { "X-Session-Token": sessionToken } : undefined,
    }
  );
  if (!response.ok && response.status !== 404) {
    throw new Error(await errorFrom(response, "Could not remove that file."));
  }
};
